import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface SectionIntroProps {
  /** Small uppercase eyebrow above the headline. */
  label: string;
  /** The headline itself — a node so sections can break lines or tint a clause. */
  title: React.ReactNode;
  /** Optional supporting paragraph. */
  body?: React.ReactNode;
  /** Optional note pinned to the right of the intro on wide screens. */
  aside?: React.ReactNode;
  align?: 'left' | 'center';
  className?: string;
}

/**
 * The shared opening beat for every home page section: eyebrow, rule, headline,
 * body. The reveal itself lives in `styles/home.scss` — this only decides when
 * to switch it on, once the intro scrolls into view.
 */
const SectionIntro: React.FC<SectionIntroProps> = ({
  label,
  title,
  body,
  aside,
  align = 'left',
  className = '',
}) => {
  const { ref, isVisible } = useScrollAnimation();
  const centered = align === 'center';

  return (
    <div
      ref={ref}
      className={`section-intro ${isVisible ? 'is-visible' : ''} ${
        aside ? 'flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8' : ''
      } ${className}`}
    >
      <div className={centered ? 'text-center max-w-3xl mx-auto' : 'max-w-2xl'}>
        <p className={`section-intro__item section-label mb-3 ${centered ? '' : ''}`}>{label}</p>
        <span className={`section-intro__rule mb-6 ${centered ? 'mx-auto' : ''}`} aria-hidden />
        <h2 className="section-intro__item section-title mb-5 text-balance">{title}</h2>
        {body && <p className={`section-intro__item section-body ${centered ? 'mx-auto' : ''}`}>{body}</p>}
      </div>
      {aside && (
        <div className="section-intro__item text-sm text-muted lg:text-right lg:max-w-xs leading-relaxed">
          {aside}
        </div>
      )}
    </div>
  );
};

export default SectionIntro;
